import React, { useState, useEffect } from 'react';
import { Theme, useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { 
  Box,
  Button,
  DialogContent, 
  DialogTitle,
  Typography,
  Stepper,
  Step,
  StepLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  SelectChangeEvent,
  TextField,
  Stack,
  Divider,
} from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import store from '../../store';
import PucTemplateSelector from '../puc-template-selector';
import allCountries from '../../utilities/all-countries';
import { Puc } from '../../models/puc';
import { getCabinetConfiguration, getCabinetsByTag, putCabinetDevice, upsertCabinet } from '../../utilities/api-utils';
import { Cabinet, ICabinet } from '../../models/cabinet';
import { getSlugId } from '../../utilities/model-utils';

interface CabinetTemplateDialogProps {
  selectedStoreId: string,
  setDialog(arg: boolean): void,
  finish(arg: string): void,
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function getStyles(country: string, smsCountries: string[], theme: Theme) {
  return {
    fontWeight:
      smsCountries.indexOf(country) === -1
        ? theme.typography.fontWeightRegular
        : theme.typography.fontWeightMedium,
  };
}

function CabinetTemplateDialog({ selectedStoreId, setDialog, finish }: CabinetTemplateDialogProps) {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState<number>(0);
  const [deviceSerial, setDeviceSerial] = useState<string>('');
  const [eloSerialErrorText, setEloSerialErrorText] = useState<string>('');
  const [storeIdErrorText, setStoreIdErrorText] = useState<string>('');
  const [descriptionErrorText, setDescriptionErrorText] = useState<string>('');
  const [businessNameErrorText, setBusinessNameErrorText] = useState<string>('');
  const [businessAddressErrorText, setBusinessAddressErrorText] = useState<string>('');
  const [businessPhoneErrorText, setBusinessPhoneErrorText] = useState<string>('');
  const [cabinetLocationErrorText, setCabinetLocationErrorText] = useState<string>('');
  const [brandErrorText, setBrandErrorText] = useState<string>('');
  const [orderManagerCodeErrorText, setOrderManagerCodeErrorText] = useState<string>('');
  const [hasEloSerialError, setHasEloSerialError] = useState<boolean>(false);
  const [hasStoreIdError, setHasStoreIdError] = useState<boolean>(false);
  const [hasDescriptionError, setHasDescriptionError] = useState<boolean>(false);
  const [hasBusinessNameError, setHasBusinessNameError] = useState<boolean>(false);
  const [hasBusinessAddressError, setHasBusinessAddressError] = useState<boolean>(false);
  const [hasBusinessPhoneError, setHasBusinessPhoneError] = useState<boolean>(false);
  const [hasCabinetLocationError, setHasCabinetLocationError] = useState<boolean>(false);
  const [hasBrandError, setHasBrandError] = useState<boolean>(false);
  const [hasOrderManagerCodeError, setHasOrderManagerCodeError] = useState<boolean>(false);
  const [hasLighting, setHasLighting] = useState<boolean>(false);
  const [lightingColorOpen, setLightingColorOpen] = useState<string>('#ffffff');
  const [hasLightingColorOpenError, setHasLightingColorOpenError] = useState<boolean>(false);
  const [lightingColorOpenErrorText, setLightingColorOpenErrorText] = useState<string>('');
  const [lightingColorClosed, setLightingColorClosed] = useState<string>('#ffffff');
  const [hasLightingColorClosedError, setHasLightingColorClosedError] = useState<boolean>(false);
  const [lightingColorClosedErrorText, setLightingColorClosedErrorText] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [businessName, setBusinessName] = useState<string>('');
  const [businessAddress, setBusinessAddress] = useState<string>('');
  const [businessPhone, setBusinessPhone] = useState<string>('');
  const [cabinetLocation, setCabinetLocation] = useState<string>('');
  const [cabinetBrand, setCabinetBrand] = useState<string>('carterhoffmann');
  const [orderManagerCode, setOrderManagerCode] = useState<string>('008');
  const [orderManagerQrCode, setOrderManagerQrCode] = useState<string>('');
  const [smsCountries, setSmsCountries] = useState<Array<string>>([]);
  const [enableSms, setEnableSms] = useState<boolean>(true);
  const [enableOrderPrint, setEnableOrderPrint] = useState<boolean>(false);
  const [selectedPuc, setSelectedPuc] = useState<Puc>();
  const [fullPucTemplates, setFullPucTemplates] = useState<Puc[]>([]);
  const [nextEnabled, setNextEnabled] = useState<boolean>(false);
  const [temperature, setTemperature] = useState<string>('');
  const [temperatureUnit, setTemperatureUnit] = useState<boolean>(false);
  const [enableTransferToCounter, setEnableTransferToCounter] = useState<boolean>(false);
  const [alwaysTransferToCounter, setAlwaysTransferToCounter] = useState<boolean>(false);
  const [expirePickups, setExpirePickups] = useState<boolean>(false);
  const [crewHome, setCrewHome] = useState<boolean>(false);
  const [pickupHome, setPickupHome] = useState<boolean>(false);
  const [hasQueueHoldMinutesError, setHasQueueHoldMinutesError] = useState<boolean>(false);
  const [hasCabinetHoldMinutesError, setHasCabinetHoldMinutesError] = useState<boolean>(false);
  const [queueHoldMinutesErrorText, setQueueHoldMinutesErrorText] = useState<string>('');
  const [cabinetHoldMinutesErrorText, setCabinetHoldMinutesErrorText] = useState<string>('');
  const [queueHoldMinutes, setQueueHoldMinutes] = useState<string>('60');
  const [cabinetHoldMinutes, setCabinetHoldMinutes] = useState<string>('30');
  
  const getCabinetTemplates = async () => {
    const tempFulls = [];
    const cloudKey = store.getState().authorization.cloudKey;
    const fullCabs = await getCabinetsByTag(cloudKey, 'puc_tool_template_full');
    
    for (const cab of fullCabs.data) {
      const cabConfig = await getCabinetConfiguration(cloudKey, cab.id);
      
      if (cabConfig.data.pucs[0]) {
        const newPuc = Puc.fromObject(cabConfig.data.pucs[0]);
        tempFulls.push(newPuc);
      }
    }
    
    setFullPucTemplates(tempFulls);
  };

  const validate = () => {
    const invalidDetails = !deviceSerial || !description || !businessName || !businessAddress || !businessPhone || !cabinetLocation || !orderManagerCode || !cabinetBrand;
    if (activeStep === 0 && invalidDetails) {
      setNextEnabled(false);
    } else {
      setNextEnabled(true);
    }
  };
  
  useEffect(() => {
    validate();
  }, [activeStep, deviceSerial, description, businessAddress, businessName, businessPhone, cabinetLocation, cabinetBrand, orderManagerCode]);

  useEffect(() => {
    const asyncGetCabs = async() => {
      await getCabinetTemplates();
    };

    asyncGetCabs();
  }, []);

  const upsertNewCabinet = async(cabinetObject: Cabinet) => {
    if (!cabinetObject) {
      return;
    }

    cabinetObject.pucs = [selectedPuc!];
    
    const payload = Cabinet.payload(cabinetObject, true);

    delete payload.properties.app.store_properties;
    delete payload.properties.app.locale;
    delete payload.properties.app.organization_order_sources;
    delete payload.properties.app.order.code_start;
    delete payload.properties.app.order.code_end;

    // Set timeout_queue from queueHoldMinutes value
    // Displayed value is in minutes; stored value is in seconds
    payload.properties.app.order.timeout_queue = (parseInt(queueHoldMinutes) * 60).toString();
    // Set timeout_cabinet from cabinetHoldMinutes value
    // Displayed value is in minutes; stored value is in seconds
    payload.properties.app.order.timeout_cabinet = (parseInt(cabinetHoldMinutes) * 60).toString();

    const response = await upsertCabinet(store.getState().authorization.cloudKey, payload);
    if (response?.status !== 201) {
      let detailString = '';
      Object.keys(response.response.data.details).forEach(k => {
        detailString += response.response.data.details[k];
      });
      alert(`\n${response.response.data.error}:\n${detailString}`);
    } else {
      const deviceResponse = await putCabinetDevice(store.getState().authorization.cloudKey, response.data.id, description, deviceSerial);
      if (deviceResponse?.status !== 201) {
        let detailString = '';
        Object.keys(deviceResponse.response.data.details).forEach(k => {
          detailString += deviceResponse.response.data.details[k];
        });
        alert(`\n${deviceResponse.response.data.error}:\n${detailString}`);
      }
      
      finish(response.data.id);
    }
  };
  
  const handleNext = async () => {
    if (isLastStep()) {
      const cabInterface: ICabinet = {
        id: getSlugId(),
        store_id: selectedStoreId,
        description: description,
        enable_order_sms: enableSms,
        enable_order_print: enableOrderPrint,
        pucs: [selectedPuc!],
        sms_countries: smsCountries,
        business_address: businessAddress,
        business_phone: businessPhone,
        business_name: businessName,
        cabinet_location: cabinetLocation,
        lockers: [],
        organization_order_sources: {},
        locale: 'en',
        store_properties: {enable_order_creation_from_ui_app: true},
        properties: {
          app: {
            brand: cabinetBrand,
            order: {
              code_end: 999,
              code_start: 100,
              timeout_queue: queueHoldMinutes,
              timeout_cabinet: cabinetHoldMinutes,
            },
            passwords: {
              codes: {
                [orderManagerCode]: 'display_order_manager'
              },
              barcodes: {
                [orderManagerQrCode]: 'display_order_manager'
              }
            },
            locale: 'en',
            store_properties: {
              enable_order_creation_from_ui_app: false
            },
            crew_home: (!crewHome ? 'manage' : 'create_new'),
            pickup_home: (!pickupHome ? 'start' : 'select_pickup'),
            expire_pickups: expirePickups,
            enable_transfer_to_counter: enableTransferToCounter,
            always_transfer_to_counter: alwaysTransferToCounter
          },
          modbus_connection: {
            retries: 5,
            timeout_ms: 250
          },
          serial_connection: {
            parity: 'even',
            baud_rate: 115200,
            data_bits: 8,
            stop_bits: 1
          }
        }
      };

      let mergedObject = cabInterface.properties;

      // If cabinet has lighting, append the default lighting parameters
      if (hasLighting) {
        const lightingParams = {
          "lighting": {
            "color_open": lightingColorOpen,
            "color_closed": lightingColorClosed,
            "fade_time_ms": 2000,
            "leds_per_locker": 30
          }
        };
        mergedObject = { ...mergedObject, ...lightingParams};
      }

      // If temperature value is present, append all the temperature properties
      if (temperature) {
        // Add the hardware temp object
        const heatingParams = {"hardware": {"global": {"lockers_heat_set": parseInt(temperature), "lockers_heat_enable": "on"}}};
        // Temperature Unit toggled "on" for celsius, or "off" for fahrenheit. Append properties.app with the units object
        const tempUnit = (!temperatureUnit) ? 'fahrenheit' : 'celsius';
        const tempUnits = {"units": {"temperature": tempUnit}};

        mergedObject.app = { ...mergedObject.app, ...tempUnits};
        mergedObject = { ...mergedObject, ...heatingParams};
      }

      cabInterface.properties = mergedObject;
      const cab = new Cabinet(cabInterface);
      cab.id = '';
      await upsertNewCabinet(cab);
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  
  const totalSteps = () => {
    return 2;
  };

  const isLastStep = () => {
    return activeStep === totalSteps() - 1;
  };

  const changeSmsCountries = (event: SelectChangeEvent<typeof smsCountries>) => {
    const {
      target: { value },
    } = event;
    const currentList = typeof value === 'string' ? value.split(',') : value;
    setSmsCountries(currentList);
  };

  const isBackDisabled = () => {
    return activeStep === 0;
  };

  const addPuc = (puc: Puc) => {
    setSelectedPuc(puc);
    validate();
  };

  return (
    <Box height='100%'>
      <DialogTitle
        display='flex'
        flexDirection='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{
          height: '100px',
          width: '100%'
        }}>
        <div style={{
            fontWeight: '500',
            color: '#121F35',
            fontSize: '30px'
          }}>
          Cabinet Creator
        </div>
        <Button 
          sx={{
            borderRadius: '5px',
            width: 'auto',
            height: 'auto',
            padding: '0',
            minWidth: '0',
            '&:hover': {
              opacity: '.5',
            }
          }}
          onClick={() => {
            setDialog(false);
          }}>
          <CloseIcon
            sx={{
              padding: '0',
              fontSize: '2rem',
              color: '#121F35',
              width: '40px',
              height: '40px',
            }} />
        </Button>
      </DialogTitle>
      <DialogContent
        sx={{
          height: '650px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}>
        <div style={{width: '100%'}}>
          <Stepper activeStep={activeStep}>
            <Step>
              <StepLabel>Cabinet Details</StepLabel>
            </Step>
            <Step>
              <StepLabel>Select Main Puc</StepLabel>
            </Step>
          </Stepper>
        </div>
        {(() => {
          switch(activeStep) {
            case 0:
              return (
                <Stack p={2}
                  direction="row"
                  divider={<Divider orientation="vertical" flexItem />}
                  spacing={2}>
                  <Box
                      sx={{
                        width: '500px',
                        display: 'flex',
                        flexDirection: 'column',
                      }}>
                    <TextField
                      error={hasEloSerialError}
                      helperText={eloSerialErrorText}
                      inputProps={{ maxLength: 24 }}
                      sx={{
                        width: '100%',
                        marginBottom: '50px',
                      }}
                      label='ELO Device Serial'
                      required
                      value={deviceSerial}
                      onBlur = {(e) => {
                        if (!e.target.value) {
                          setEloSerialErrorText('ELO Serial Number is required');
                          setHasEloSerialError(true);
                        } else {
                          setEloSerialErrorText('');
                          setHasEloSerialError(false);
                        }
                      }}
                      onChange={(e) => {
                        setDeviceSerial(e.target.value);
                      }} />

                    <div style={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      marginBottom: '12px',
                      marginTop: '-28px'
                    }}>
                      <div style={{
                        width: '20%'
                      }}>
                        <Typography>Lighting</Typography>
                        <Switch
                          checked={hasLighting}
                          onChange={(e) => {
                            setHasLighting(e.target.checked);
                        }} />
                      </div>
                      <div style={{
                        width: '80%',
                        display: 'inline'
                      }}>
                        <TextField 
                          sx={{
                            width: '190px',
                            marginRight: '8px'
                          }}
                          error={hasLightingColorOpenError}
                          helperText={lightingColorOpenErrorText}
                          disabled={!hasLighting}
                          required={hasLighting}
                          label="Lighting Color Open"
                          value={lightingColorOpen}
                          onBlur = {(e) => {
                            if (!e.target.value) {
                              setLightingColorOpenErrorText('Lighting Color Open is required');
                              setHasLightingColorOpenError(true);
                            } else {
                              setLightingColorOpenErrorText('');
                              setHasLightingColorOpenError(false);
                            }
                          }}
                          onChange={(e) => {
                            setLightingColorOpen(e.target.value);
                          }} />

                        <TextField 
                          sx={{
                            width: '190px',
                            marginLeft: '8px'
                          }}
                          error={hasLightingColorClosedError}
                          helperText={lightingColorClosedErrorText}
                          disabled={!hasLighting}
                          required={hasLighting}
                          label="Lighting Color Closed"
                          value={lightingColorClosed}
                          onBlur = {(e) => {
                            if (!e.target.value) {
                              setLightingColorClosedErrorText('Lighting Color Closed is required');
                              setHasLightingColorClosedError(true);
                            } else {
                              setLightingColorClosedErrorText('');
                              setHasLightingColorClosedError(false);
                            }
                          }}
                          onChange={(e) => {
                            setLightingColorClosed(e.target.value);
                          }} />
                      </div>
                    </div>
                    <div style={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      marginBottom: '12px'
                    }}>
                      <div style={{
                        display: 'flex',
                        width: '50%'
                      }}>
                        <TextField 
                          sx={{
                            width: '120px',
                            marginRight: '15px'
                          }}
                          label="Temperature"
                          value={temperature}
                          onChange={(e) => {
                            setTemperature(e.target.value);
                          }} />
                        <div style={{
                          display: 'flex',
                          marginTop: '10px'
                        }}>
                          <Typography style={{
                            marginTop: '8px'
                          }}>F</Typography>
                          <Switch 
                            checked={temperatureUnit}
                            onChange={(e) => {
                              setTemperatureUnit(e.target.checked);
                            }} />
                          <Typography style={{
                            marginTop: '8px'
                          }}>C</Typography>
                        </div>
                      </div>
                    </div>
                    
                    
                    <div style={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      marginBottom: '16px'
                    }}>
                      <div style={{
                        width: '50%'
                      }}>
                        <Typography>Enable Order Print</Typography>
                        <Switch
                          checked={enableOrderPrint}
                          onChange={(e) => {
                            setEnableOrderPrint(e.target.checked);
                        }} />
                      </div>
                      <div style={{
                        width: '50%'
                      }}>
                        <Typography>Expire Pickups</Typography>
                        <Switch
                          checked={expirePickups}
                          onChange={(e) => {
                            setExpirePickups(e.target.checked);
                        }} />
                      </div>
                    </div>
                    <div style={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      marginBottom: '28px',
                    }}>
                      <div style={{
                        width: '30%'
                      }}>
                        <Typography>Enable SMS</Typography>
                        <Switch
                          checked={enableSms}
                          onChange={(e) => {
                            setEnableSms(e.target.checked);
                        }} />
                      </div>
                      <div style={{
                        width: '70%',
                      }}>
                        <Typography>SMS Countries</Typography>
                        <Select
                          multiple
                          value={smsCountries}
                          onChange={changeSmsCountries}
                          input={<OutlinedInput label="Country" />}
                          MenuProps={MenuProps}
                          sx={{
                            width: '100%',
                          }}>
                          {allCountries.map((country) => (
                            <MenuItem
                              key={country.iso2}
                              value={country.iso2}
                              style={getStyles(country.iso2, smsCountries, theme)}>
                              {country.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </div>
                    </div>

                    <div style={{
                      width: '100%'
                    }}>
                      <TextField
                        error={hasQueueHoldMinutesError}
                        helperText={queueHoldMinutesErrorText}
                        inputProps={{ maxLength: 10 }}
                        sx={{
                          width: '40%',
                          marginBottom: '16px',
                          marginRight: '5%'
                        }}
                        required
                        label='Queue Hold Time (minutes)'
                        value={queueHoldMinutes}
                        onBlur = {(e) => {
                          if (!e.target.value) {
                            setQueueHoldMinutesErrorText('Queue Hold Minutes is required');
                            setHasQueueHoldMinutesError(true);
                          } else if (parseInt(e.target.value) < 5 || parseInt(e.target.value) > 1440) {
                            setQueueHoldMinutesErrorText('Queue Hold Time should be between 5 and 1440 minutes');
                            setHasQueueHoldMinutesError(true);
                          } else {
                            setQueueHoldMinutesErrorText('');
                            setHasQueueHoldMinutesError(false);
                          }
                        }}
                        onChange = {(e) => {
                          setQueueHoldMinutes(e.target.value);
                        }} />

                      <TextField
                        error={hasCabinetHoldMinutesError}
                        helperText={cabinetHoldMinutesErrorText}
                        inputProps={{ maxLength: 10 }}
                        sx={{
                          width: '40%',
                          marginBottom: '16px',
                          marginLeft: '5%'
                        }}
                        required
                        label='Cabinet Hold Time (minutes)'
                        value={cabinetHoldMinutes}
                        onBlur = {(e) => {
                          if (!e.target.value) {
                            setCabinetHoldMinutesErrorText('Cabinet Hold Minutes is required');
                            setHasCabinetHoldMinutesError(true);
                          } else if (parseInt(e.target.value) < 5 || parseInt(e.target.value) > 720) {
                            setCabinetHoldMinutesErrorText('Cabinet Hold Time should be between 5 and 720 minutes');
                            setHasCabinetHoldMinutesError(true);
                          } else {
                            setCabinetHoldMinutesErrorText('');
                            setHasCabinetHoldMinutesError(false);
                          }
                        }}
                        onChange = {(e) => {
                          setCabinetHoldMinutes(e.target.value);
                        }} />
                      </div>

                    <div style={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      marginBottom: '16px'
                    }}>
                      <div style={{
                        width: '50%'
                      }}>
                        <Typography>Enable Transfer to Counter</Typography>
                        <Switch
                          checked={enableTransferToCounter}
                          onChange={(e) => {
                            setEnableTransferToCounter(e.target.checked);
                        }} />
                      </div>
                      <div style={{
                        width: '50%'
                      }}>
                        <Typography>Always Transfer to Counter</Typography>
                        <Switch
                          checked={alwaysTransferToCounter}
                          onChange={(e) => {
                            setAlwaysTransferToCounter(e.target.checked);
                        }} />
                      </div>
                    </div>

                    <div style={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between'
                    }}>
                      <div style={{
                        width: '50%'
                      }}>
                        <Typography>Crew Home Screen</Typography>
                        <div style={{
                          display: 'flex',
                        }}>
                          <Typography style={{
                            marginTop: '8px'
                          }}>Manage</Typography>
                          <Switch 
                            checked={crewHome}
                            onChange={(e) => {
                              setCrewHome(e.target.checked);
                            }} />
                          <Typography style={{
                            marginTop: '8px'
                          }}>Create New</Typography>
                        </div>
                      </div>
                      <div style={{
                        width: '50%'
                      }}>
                        <Typography>Pickup Home Screen</Typography>
                        <div style={{
                          display: 'flex',
                        }}>
                          <Typography style={{
                            marginTop: '8px'
                          }}>Home</Typography>
                          <Switch 
                            checked={pickupHome}
                            onChange={(e) => {
                              setPickupHome(e.target.checked);
                            }} />
                          <Typography style={{
                            marginTop: '8px'
                          }}>Select Pickup</Typography>
                        </div>
                      </div>
                    </div>
                  </Box>
                  <Box
                    sx={{
                      width: '500px',
                      display: 'flex',
                      flexDirection: 'column',
                    }}>
                    <TextField
                      error={hasDescriptionError}
                      helperText={descriptionErrorText}
                      inputProps={{ maxLength: 100 }}
                      sx={{
                        width: '450px',
                        marginBottom: '25px',
                        marginLeft: '20px'
                      }}
                      required
                      label='Description'
                      value={description}
                      onBlur = {(e) => {
                        if (!e.target.value) {
                          setDescriptionErrorText('Description is required');
                          setHasDescriptionError(true);
                        } else {
                          setDescriptionErrorText('');
                          setHasDescriptionError(false);
                        }
                      }}
                      onChange = {(e) => {
                        setDescription(e.target.value);
                      }} />
                    <TextField
                      error={hasBusinessNameError}
                      helperText={businessNameErrorText}
                      inputProps={{ maxLength: 50 }}
                      sx={{
                        width: '450px',
                        marginBottom: '25px',
                        marginLeft: '20px'
                      }}
                      required
                      label='Business Name'
                      value={businessName}
                      onBlur = {(e) => {
                        if (!e.target.value) {
                          setBusinessNameErrorText('Business Name is required');
                          setHasBusinessNameError(true);
                        } else {
                          setBusinessNameErrorText('');
                          setHasBusinessNameError(false);
                        }
                      }}
                      onChange = {(e) => {
                        setBusinessName(e.target.value);
                      }} />
                    <TextField
                      error={hasBusinessAddressError}
                      helperText={businessAddressErrorText}
                      inputProps={{ maxLength: 100 }}
                      sx={{
                        width: '450px',
                        marginBottom: '25px',
                        marginLeft: '20px'
                      }}
                      required
                      label='Business Address'
                      value={businessAddress}
                      onBlur = {(e) => {
                        if (!e.target.value) {
                          setBusinessAddressErrorText('Business Address is required');
                          setHasBusinessAddressError(true);
                        } else {
                          setBusinessAddressErrorText('');
                          setHasBusinessAddressError(false);
                        }
                      }}
                      onChange = {(e) => {
                        setBusinessAddress(e.target.value);
                      }} />
                    <TextField
                      error={hasBusinessPhoneError}
                      helperText={businessPhoneErrorText}
                      inputProps={{ maxLength: 15 }}
                      sx={{
                        width: '450px',
                        marginBottom: '25px',
                        marginLeft: '20px'
                      }}
                      required
                      label='Business Phone'
                      value={businessPhone}
                      onBlur = {(e) => {
                        if (!e.target.value) {
                          setBusinessPhoneErrorText('Business Phone is required');
                          setHasBusinessPhoneError(true);
                        } else {
                          setBusinessPhoneErrorText('');
                          setHasBusinessPhoneError(false);
                        }
                      }}
                      onChange = {(e) => {
                        setBusinessPhone(e.target.value);
                      }} />
                    <TextField
                      error={hasCabinetLocationError}
                      helperText={cabinetLocationErrorText}
                      inputProps={{ maxLength: 75 }}
                      sx={{
                        width: '450px',
                        marginBottom: '25px',
                        marginLeft: '20px'
                      }}
                      required
                      label='Location'
                      value={cabinetLocation}
                      onBlur = {(e) => {
                        if (!e.target.value) {
                          setCabinetLocationErrorText('Location is required');
                          setHasCabinetLocationError(true);
                        } else {
                          setCabinetLocationErrorText('');
                          setHasCabinetLocationError(false);
                        }
                      }}
                      onChange = {(e) => {
                        setCabinetLocation(e.target.value);
                      }} />
                    <TextField
                      error={hasBrandError}
                      helperText={brandErrorText}
                      inputProps={{ maxLength: 36 }}
                      sx={{
                        marginLeft: '20px',
                        width: '450px',
                        marginBottom: '25px'
                      }}
                      required
                      label='Brand'
                      value={cabinetBrand}
                      onBlur = {(e) => {
                        if (!e.target.value) {
                          setBrandErrorText('Brand is required');
                          setHasBrandError(true);
                        } else {
                          setBrandErrorText('');
                          setHasBrandError(false);
                        }
                      }}
                      onChange = {(e) => {
                        setCabinetBrand(e.target.value);
                      }} />
                  </Box>
                </Stack>
              );
            case 1:
              return (
                <div>
                  <PucTemplateSelector pucTemplates={fullPucTemplates} onPucSelected={addPuc} />
                </div>
              );
          }
        })()}
        <Box sx={{
          display: 'flex',
          flexDirection: 'row'
          }}>
          <Button
            disabled={isBackDisabled()}
            onClick={handleBack}
            sx={{ mr: 1 }}>
            <KeyboardArrowLeft /> Back
          </Button>
          <Box sx={{ flex: '1 1 auto' }} />
          <Button
            disabled={!nextEnabled}
            onClick={async() => { await handleNext(); }}>
            {isLastStep()
              ? 'Finish'
              : 'Next'}
              <KeyboardArrowRight />
          </Button>
        </Box>
      </DialogContent>
    </Box>
  );
}

export default CabinetTemplateDialog;
